import * as React from "react";

import { textContainerClassName } from "@1js/acui-common";

import {
  useAriaDescription,
  useAriaKeyShortcuts,
  useAriaLabel,
  useDataAutomationType,
  useDataNav,
  useDataUniqueId,
  useFunctionGetInputMethod,
  useShorthandFontIcon,
} from "../../../hooks";
import type { AppButtonProps, AppButtonState } from "./AppButton.types";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { useCommonButtonStyles } from "../../../styling/commonButtonStyles.styles";

export const useAppButtonState = (
  props: AppButtonProps,
  state: AppButtonState
): AppButtonState => {
  const {
    dataAutomationType,
    dataNav,
    fullDescriptionLabel,
    id,
    keyShortcuts,
    label,
    location,
    onExecute,
    onMouseDown,
    shouldTakeFocus,
    takeFocusOnClick,
    hideLabel,
    toggled = false,
    ariaDescription,
    customTooltip,
    iconColor,
    flipIconInRtl,
    onClick,
    isDoubleClickEnabled = false,
    hideIcon,
    disabled,
  } = props;

  // Icon only considerations
  const iconOnly = !!hideLabel;

  // Always set iconOnly to false on the state, we want to render the children always even if it's "undefined"
  state.iconOnly = false;
  state.root.children = !iconOnly
    ? state.root.children || (
        <span className={textContainerClassName}>{label}</span>
      )
    : undefined;

  useAriaLabel(state, fullDescriptionLabel || label);
  useAriaKeyShortcuts(state, keyShortcuts);
  useDataAutomationType(state, dataAutomationType ?? "AppButton");
  useDataUniqueId(state, id, location);
  useDataNav(state, dataNav);
  useAriaDescription(state, ariaDescription || customTooltip);

  const context = React.useContext(AppContext);
  const hideIconUsingToggleEnabled = !!checkExperiment(
    context,
    ribbonExperiments.fluentV9ToggleIconEnabled
  );
  const shouldHideIcon = hideIconUsingToggleEnabled && hideIcon;
  useShorthandFontIcon(state, iconColor, flipIconInRtl, shouldHideIcon);
  const getInputMethod = useFunctionGetInputMethod(state);

  const isHideMenuOnPrimaryButtonClickFixEnabled = !!checkExperiment(
    context,
    ribbonExperiments.hideMenuOnPrimaryButtonClickFixEnabled
  );

  const isDisabledControlsChangeGateEnabled = !!checkExperiment(
    context,
    ribbonExperiments.isDisabledControlsChangeGateEnabled
  );

  state.root.onClick = React.useCallback(
    (
      ev: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement, MouseEvent>
    ) => {
      if (isDisabledControlsChangeGateEnabled && disabled) {
        return;
      }
      if (onExecute) {
        onExecute({
          id,
          location,
          toggled,
          inputMethod: getInputMethod(ev),
          shouldTakeFocus,
          triggerTime: ev && ev.timeStamp,
          event: ev?.nativeEvent,
        });

        if (isHideMenuOnPrimaryButtonClickFixEnabled) {
          // In addition to what to the onExecute does, we also want to call the onClick
          // for cases like the split button where the onClick is used to close the dropdown menu
          onClick?.(ev);
        }
      } else {
        onClick?.(ev);
      }
    },
    [
      onExecute,
      id,
      location,
      toggled,
      getInputMethod,
      shouldTakeFocus,
      onClick,
      isHideMenuOnPrimaryButtonClickFixEnabled,
      disabled,
      isDisabledControlsChangeGateEnabled,
    ]
  );

  state.root.onMouseDown = React.useCallback(
    (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onMouseDown) {
        onMouseDown(ev);
      }
      if (!takeFocusOnClick) {
        ev.preventDefault();
      }
    },
    [onMouseDown, takeFocusOnClick]
  );

  const hideCustomTooltipEnabled = !!checkExperiment(
    context,
    ribbonExperiments.hideCustomTooltipInV9AppControlsEnabled
  );
  // Apply style to hide customTooltip when label is hidden
  useCommonButtonStyles(state, iconOnly, hideCustomTooltipEnabled);

  state.root.onDoubleClick = React.useCallback(
    (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (isDoubleClickEnabled && onExecute) {
        onExecute({
          id,
          location,
          toggled,
          inputMethod: getInputMethod(ev),
          shouldTakeFocus,
          triggerTime: ev && ev.timeStamp,
          event: ev?.nativeEvent,
          isDoubleClick: true,
        });
      }
    },
    [
      getInputMethod,
      id,
      isDoubleClickEnabled,
      location,
      onExecute,
      shouldTakeFocus,
      toggled,
    ]
  );

  return state;
};
